import * as Sentry from '@sentry/vue';
import { getEnvironment } from './cookie';

/**
 * Configura o Sentry para monitoramento de erros e performance
 * @param {Object} app - Instância da aplicação Vue
 */
export function initSentry(app) {
  const environment =
    typeof getEnvironment === 'function'
      ? getEnvironment()
      : process.env.NODE_ENV || 'development';

  Sentry.init({
    app,
    dsn: 'https://8c642bac301e1b8418b66297c5661eca@o4509357272334336.ingest.us.sentry.io/4509357273382912',

    // Configurações do ambiente
    environment,
    release: process.env.VUE_APP_VERSION || '1.0.0',

    // Taxa de amostragem de performance (0.1 = 10%)
    tracesSampleRate: environment === 'production' ? 0.1 : 0.5,

    // Configurações de coleta de dados
    sendDefaultPii: true,
    autoSessionTracking: true,

    // Configurações de depuração
    debug: environment !== 'production',

    // Configurações de Vue
    trackComponents: true,
    logErrors: environment !== 'production',
    attachProps: true,
    attachMetrics: true,

    // Ignorar determinados erros (opcional)
    ignoreErrors: [
      'Network Error',
      'ResizeObserver loop limit exceeded',
      /^cors error/i,
      /^aborted/i,
      /^cancelada/i,
    ],

    // Hooks para personalização
    beforeSend(event, hint) {
      // Você pode modificar ou descartar eventos aqui
      const error = hint?.originalException;

      // Exemplo: Não relatar erros 401/403 (problemas de autenticação comuns)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return null;
      }

      // Adicionar tags e contextos personalizados
      if (event.tags) {
        event.tags.source = 'vue-app';
      }

      return event;
    },
  });

  // Adicionar o usuário ao contexto do Sentry
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const user = JSON.parse(userData);

      // Adicionar dados do usuário ao Sentry
      Sentry.setUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.accountRole?.name,
      });
    } catch (e) {
      console.warn('Erro ao carregar dados do usuário para o Sentry:', e);
    }
  }

  // Configuração para atualizar contexto do usuário quando mudar
  window.addEventListener('userData-updated', () => {
    updateUserContext();
  });
}

/**
 * Atualiza o contexto do usuário no Sentry
 */
export function updateUserContext() {
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const user = JSON.parse(userData);

      Sentry.setUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.accountRole?.name,
      });
    } catch (e) {
      console.warn('Erro ao atualizar dados do usuário no Sentry:', e);
    }
  } else {
    // Se não houver dados do usuário, limpar o contexto
    Sentry.setUser(null);
  }
}

/**
 * Captura um erro no Sentry com contextos adicionais
 * @param {Error} error - O erro a ser capturado
 * @param {Object} contextData - Dados adicionais de contexto
 */
export function captureError(error, contextData = {}) {
  if (!error) return;

  // Adicionar contexto extra
  if (contextData) {
    Sentry.setContext('error_details', contextData);
  }

  // Capturar o erro
  Sentry.captureException(error);
}

/**
 * Registra uma mensagem no Sentry
 * @param {string} message - A mensagem a ser registrada
 * @param {string} level - Nível do log ('info', 'warning', 'error')
 * @param {Object} contextData - Dados adicionais de contexto
 */
export function logMessage(message, level = 'info', contextData = {}) {
  if (contextData) {
    Sentry.setContext('message_details', contextData);
  }

  Sentry.captureMessage(message, level);
}

/**
 * Registra uma transação de performance
 * @param {string} name - Nome da transação
 * @param {Function} operation - Função a ser executada e medida
 * @param {Object} contextData - Dados adicionais de contexto
 * @returns {Promise} - Resultado da operação
 */
export async function trackPerformance(name, operation, contextData = {}) {
  const transaction = Sentry.startSpan({
    name,
    op: 'function',
    data: contextData,
  });

  try {
    return await operation();
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  } finally {
    transaction.end();
  }
}

export default {
  initSentry,
  updateUserContext,
  captureError,
  logMessage,
  trackPerformance,
};
